import React, { FC, useRef, SyntheticEvent, ChangeEvent, Fragment } from 'react';
import { Slider, IconButton, Grid, Divider } from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import { selectedElement, updateContentElementById } from 'features/document/documentSlice';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { colorPalette, docGeniusPalette } from 'styles/partials/colors';
import { useAppDispatch } from 'app/hooks';
import { Tag } from 'components/RadioTag/RadioTag';
import StyleCheckbox from 'components/StyleCheckbox/StyleCheckbox';
import { isImageElement } from 'utils/typeGuards';
import AlignmentRadioGroup from 'components/AlignmentRadioGroup/AlignmentRadioGroup';
import { useParams } from 'react-router-dom';
import { useDeleteFileMutation, useGetFileQuery, usePostFileMutation } from 'features/files/filesApi';
import { toBase64 } from 'utils/fileToBase64';
import { Subtitle, Title } from '../../Sidebar.styled';
import Button from 'components/Button/Button';
import { GridContainer, GridSubcontainer, HiddenButton, SubtitleSize } from './ImageSidebar.styled';

interface ImageSideBarProps {
  currentElement: selectedElement;
}

const ImageSidebar: FC<ImageSideBarProps> = ({ currentElement }: ImageSideBarProps) => {
  if (!isImageElement(currentElement.data)) {
    return null;
  }

  const {
    content: { id: imageId },
    style,
  } = currentElement.data;
  const { data, isLoading } = useGetFileQuery(Number(imageId), { skip: imageId === null });

  const { id } = useParams();
  const [postFile] = usePostFileMutation();
  const [deleteFile] = useDeleteFileMutation();

  const dispatch = useAppDispatch();
  const { justifyContent, padding, width } = style;
  const hasPadding = padding.length > 0;
  const widthValue = Number(parseInt(width, 10)); // converts '100%' to 100
  const imageWidth = widthValue > 0 ? widthValue : 100;

  const hiddenImageInput = useRef<HTMLInputElement>(null);

  const styleOptions = {
    justifyContent: [
      { label: 'left', value: 'left' },
      { label: 'center', value: 'center' },
      { label: 'right', value: 'right' },
    ],
    padding: {
      label: 'Add Image spacing',
      value: '1rem',
    },
  };

  const handleClick = () => {
    hiddenImageInput.current?.click();
  };

  const handleDelete = async () => {
    if (!id) {
      dispatch(
        updateContentElementById({
          content: {
            id: null,
          },
        })
      );
      await deleteFile(Number(imageId));
    } else {
      dispatch(
        updateContentElementById({
          content: {
            id: null,
          },
        })
      );
    }
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files !== null) {
      const srcName = event.currentTarget.files[0].name;
      const imageBase64 = await toBase64(event.currentTarget.files[0]);
      const imageContent = imageBase64.split(',')[1];
      const fileResult = await postFile({
        type: 'jpeg',
        fileName: srcName,
        fileContent: imageContent,
      }).unwrap();
      if (imageId && fileResult && !id) {
        deleteFile(imageId);
      }
      dispatch(
        updateContentElementById({
          content: {
            id: fileResult.data?.id,
          },
        })
      );
    }
    event.target.value = '';
  };

  const handleChangeImgSize = (_event: Event | SyntheticEvent<Element, Event>, val: number | number[]) => {
    if (typeof val === 'number') {
      dispatch(
        updateContentElementById({
          style: {
            ['width']: `${val}%`,
          },
        })
      );
    }
  };

  const handleUpdateByProperty = (property: string) => {
    return (tag: Tag) => {
      dispatch(
        updateContentElementById({
          style: {
            [property]: tag.value,
          },
        })
      );
    };
  };

  const handleTogglePadding = (property: string) => {
    return (tag: Tag) => {
      dispatch(
        updateContentElementById({
          style: {
            [property]: hasPadding ? '' : tag.value,
          },
        })
      );
    };
  };

  return (
    <GridContainer container flexDirection='column' alignItems='flex-start'>
      <Grid item xs={12}>
        <Title>Image Block</Title>
      </Grid>
      <GridSubcontainer container alignItems='center'>
        <Grid item xs={12}>
          <Subtitle>Image Upload</Subtitle>
        </Grid>
        <GridSubcontainer container>
          {imageId === null || isLoading ? (
            <Grid item xs={12}>
              <HiddenButton onClick={handleClick}>
                <Button sx={{ width: '100%' }} variant='secondary'>
                  Upload Image
                  <FileUploadIcon />
                </Button>
              </HiddenButton>
            </Grid>
          ) : (
            <Fragment>
              <Grid item xs={10}>
                <img alt='uploaded' src={data?.data.url} width='80%' />
              </Grid>
              <Grid item xs={2}>
                <Grid item>
                  <IconButton disableTouchRipple onClick={handleClick}>
                    <ModeEditIcon sx={{ color: docGeniusPalette.primaryBlue }} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton disableTouchRipple onClick={handleDelete}>
                    <DeleteIcon sx={{ color: docGeniusPalette.primaryBlue }} />
                  </IconButton>
                </Grid>
              </Grid>
            </Fragment>
          )}
          <input type='file' onChange={handleUpload} ref={hiddenImageInput} hidden />
        </GridSubcontainer>
      </GridSubcontainer>
      {imageId && (
        <GridSubcontainer container>
          <Divider
            color='#C9CACB'
            flexItem
            orientation='horizontal'
            variant='middle'
            sx={{ margin: 0, width: '100%' }}
          />
          <GridSubcontainer container>
            <Grid item xs={12}>
              <Subtitle>Size</Subtitle>
            </Grid>
            <Grid item xs={12}>
              <SubtitleSize>{imageWidth}%</SubtitleSize>
              <Slider
                size='small'
                min={10}
                value={imageWidth}
                max={100}
                aria-label='Small'
                valueLabelDisplay='off'
                onChange={handleChangeImgSize}
                sx={{ '.MuiSlider-thumb': { boxShadow: 'none' } }}
              />
            </Grid>
          </GridSubcontainer>
          <GridSubcontainer container gap={2}>
            <Grid item xs={12}>
              <Subtitle>Alignment</Subtitle>
            </Grid>
            <Grid item xs={9}>
              <AlignmentRadioGroup
                selected={styleOptions.justifyContent.find(el => el.value === justifyContent) as Tag}
                handleChange={handleUpdateByProperty('justifyContent')}
              />
            </Grid>
          </GridSubcontainer>
          <GridSubcontainer color={colorPalette.darkGray1} item xs={12}>
            <StyleCheckbox
              option={styleOptions.padding}
              checked={hasPadding}
              handleChange={handleTogglePadding('padding')}
            />
          </GridSubcontainer>
        </GridSubcontainer>
      )}
    </GridContainer>
  );
};

export default ImageSidebar;
